import { Select } from "antd";
import UserTag from "components/user-tag/user-tag";
import { AcceptanceStatus } from "constants/index";
import { useProjectParticipants } from "hooks/project-participants";
import { useMemo, useState, useEffect } from "react";

const { Option } = Select;

type Props = {
  value?: any;
  notFoundContent?: any;
  onChange?: (value: any, option: any) => void;
  disabled?: boolean;
  selectedCompanyFieldId: string;
  companyNotFoundContent?: string;
  projectParticipantType: "designReviewers" | "submitterUsers";
  rcClassName?: string;
  submitterClassName?: string;
  defaultOpen?: boolean;
};

function DependedCompanyUserDropdownComponent(props: Props) {
  const {
    value,
    notFoundContent,
    onChange,
    disabled,
    selectedCompanyFieldId,
    companyNotFoundContent = "First select a Responsible Contractor",
    projectParticipantType,
    rcClassName = "w-full",
    submitterClassName = "w-full",
    defaultOpen = false
  } = props;
  const { projectParticipants } = useProjectParticipants();
  const dataLoaded = projectParticipants?.dataLoaded;

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (defaultOpen && dataLoaded) {
      setOpen(true);
    }
  }, [defaultOpen, dataLoaded]);

  const responsibleDesigners = useMemo(() => {
    if (!projectParticipants) return undefined;
    const arr = projectParticipants[projectParticipantType]?.filter(
      (user: any) =>
        selectedCompanyFieldId &&
        user &&
        user?.company?.vendor_id === selectedCompanyFieldId
    );
    return arr;
  }, [projectParticipantType, projectParticipants, selectedCompanyFieldId]);

  if (!selectedCompanyFieldId)
    return (
      <div className="flex justify-center items-center h-full w-full">
        <Select
          disabled
          allowClear
          className={rcClassName}
          options={[
            {
              label: (
                <div className="flex items-center text-ciq-red-700">
                  {companyNotFoundContent}
                </div>
              ),
              value: 1
            }
          ]}
          value={1}
        />
      </div>
    );

  return (
    <Select
      className={submitterClassName}
      loading={!dataLoaded}
      value={value || undefined}
      allowClear
      onChange={onChange}
      showSearch
      filterOption={(inputValue, option: any) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      }
      optionFilterProp="label"
      notFoundContent={notFoundContent}
      disabled={disabled}
      // antd v6 renders the Option's children as the *selected* value, so the
      // company sub-line ended up inside the closed Select (v5 clipped it to a
      // single line). Point the selected value at a name-only node; the
      // dropdown list still renders the children, sub-line included.
      optionLabelProp="selectedLabel"
      data-testid="index-select-company-user"
      {...(defaultOpen && { open, onOpenChange: setOpen })}
    >
      {(responsibleDesigners ?? []).map((user: any) => {
        // The `?? ""` fallback is unreachable: the left operand is a boolean
        // (=== / && always yield true|false, never null|undefined), so the outer
        // `??` never falls through to the literal "". v8-to-istanbul keys this
        // branch off the `??` expression's start line, so the ignore sits here.
        /* v8 ignore next 4 */
        if (
          (user.status_id === AcceptanceStatus.DEACTIVATED &&
            user.id !== value?.toString()) ??
          ""
        )
          return "";

        // The `?? ""` fallback is unreachable: the left operand is a boolean
        // (=== / && always yield true|false, never null|undefined), so the outer
        // `??` never falls through to the literal "". v8-to-istanbul keys this
        // branch off the `??` expression's start line, so the ignore sits here.
        /* v8 ignore next 4 */
        const selectedAndInactive =
          (user.status_id === AcceptanceStatus.DEACTIVATED &&
            user.id === value?.toString()) ??
          "";

        const inactiveTag = user.status_id === AcceptanceStatus.DEACTIVATED && (
          <div className="absolute flex w-full items-center justify-end pr-3 -mt-[1px]">
            <UserTag label="Inactive" />
          </div>
        );

        return (
          <Option
            key={user.id}
            value={user.id}
            label={`${user.first_name} ${user.last_name} ${user?.company?.name}`}
            selectedLabel={
              <>
                {inactiveTag}
                {user.first_name} {user.last_name}
              </>
            }
            firstName={user.first_name}
            lastName={user.last_name}
            company={user?.company}
            className={selectedAndInactive ? "hidden" : ""}
          >
            {inactiveTag}
            <div>
              {user.first_name} {user.last_name}
            </div>
            <div className="text-sm text-gray-500">{user?.company?.name}</div>
          </Option>
        );
      })}
    </Select>
  );
}

export default DependedCompanyUserDropdownComponent;
